import { useCallback, useEffect, useRef } from 'react';
import qs from 'qs';
import { redirectToAddCard } from '@ordermentum/ui-shared/helpers/urls';

type BuildReturnUrlParams = {
  retailerId: string;
  paymentRequestId?: string;
  invoiceId?: string;
  userId?: string;
  token?: string;
  totalOutstandingInvoices: number;
};

type RedirectToSecureCardPageParams = BuildReturnUrlParams & {
  purchaserId: string;
  supplierId?: string;
  canSwitch: boolean;
};

/**
 * Builds the return URL for after successful card addition.
 * If paymentRequestId or token are missing, we can't trust totalOutstandingInvoices
 * (it may just be the default 0 because the API call was skipped). In that case,
 * conservatively send the user to the outstanding invoices flow.
 */
export const buildReturnUrl = ({
  retailerId,
  paymentRequestId,
  invoiceId,
  userId,
  token,
  totalOutstandingInvoices,
}: BuildReturnUrlParams): string => {
  const queryParams = qs.stringify({
    paymentRequestId,
    invoiceId,
    userId,
    token,
  });

  const getBasePath = () => {
    // If paymentRequestId or token are missing, we can't trust totalOutstandingInvoices
    // (it may just be the default 0 because the API call was skipped).
    // Conservatively send the user to the outstanding invoices flow.
    if (!paymentRequestId || !token) {
      return `/payments/${retailerId}/outstanding-invoices`;
    }

    return totalOutstandingInvoices > 0
      ? `/payments/${retailerId}/outstanding-invoices`
      : `/payments/${retailerId}/auth`;
  };

  const basePath = getBasePath();

  return `${basePath}?${queryParams}`;
};

/**
 * Redirects to the PCI-compliant secure card page.
 */
export const redirectToSecureCardPage = ({
  retailerId,
  purchaserId,
  supplierId,
  paymentRequestId,
  invoiceId,
  userId,
  token,
  canSwitch,
  totalOutstandingInvoices,
}: RedirectToSecureCardPageParams): void => {
  const returnUrl = buildReturnUrl({
    retailerId,
    paymentRequestId,
    invoiceId,
    userId,
    token,
    totalOutstandingInvoices,
  });

  redirectToAddCard({
    retailerId,
    returnUrl,
    purchaserId,
    supplierId,
    paymentRequestId,
    token,
    canSwitchToDirect: canSwitch,
  });
};

type UsePciRedirectParams = {
  retailerId: string;
  supplierId?: string;
  paymentRequestId?: string;
  invoiceId?: string;
  userId?: string;
  token?: string;
  // Data needed to determine redirect
  isCard: boolean;
  isInvoice: boolean;
  isLoading: boolean;
  hasError: boolean;
  hasData: boolean;
  // Data needed for redirect URL
  purchaserId?: string;
  canSwitch: boolean;
  totalOutstandingInvoices: number;
  // Query param to prevent redirect loops
  defaultPaymentType?: string;
  /**
   * Flag from payment request response indicating if secure card form is enabled.
   * When paymentRequestId + token are present, this value is used instead of
   * calling useFlagCheck to avoid unnecessary authenticated API calls.
   */
  enableSecurePaymentRequest?: boolean;
};

type UsePciRedirectReturn = {
  /** Whether the PCI secure card form flag is enabled */
  isPciSecureCardFormEnabled: boolean;
  /** Whether we should redirect to the secure card page (auto-redirect on page load) */
  shouldRedirectToSecureCardPage: boolean;
  /** Manually trigger redirect to secure card page (e.g., when clicking switch button) */
  triggerRedirect: () => void;
};

/**
 * Hook to handle PCI-compliant card form redirect logic.
 * This hook is responsible for:
 * - Checking the PCI_SECURE_CARD_FORM flag (or using enableSecurePaymentRequest from payment request)
 * - Determining if auto-redirect should happen on page load
 * - Providing a function to manually trigger redirect (for switch button)
 * - Handling the redirect via useEffect to avoid side effects during render
 */
export const usePciRedirect = ({
  retailerId,
  supplierId,
  paymentRequestId,
  invoiceId,
  userId,
  token,
  isCard,
  isInvoice,
  isLoading,
  hasError,
  hasData,
  purchaserId,
  canSwitch,
  totalOutstandingInvoices,
  defaultPaymentType,
  enableSecurePaymentRequest,
}: UsePciRedirectParams): UsePciRedirectReturn => {
  const hasRedirected = useRef(false);

  // Use enableSecurePaymentRequest from payment request response
  const isPciSecureCardFormEnabled = enableSecurePaymentRequest ?? false;

  // Determine if we should auto-redirect to secure card page on page load
  // Split into readable conditions for clarity
  const hasValidData = !hasError && hasData && !isLoading;
  const isCardPaymentFlow = isCard && !isInvoice;
  const isNotSwitchingFromDirect = defaultPaymentType !== 'direct';

  const shouldRedirectToSecureCardPage =
    hasValidData &&
    isCardPaymentFlow &&
    isNotSwitchingFromDirect &&
    isPciSecureCardFormEnabled;

  /**
   * Manually trigger redirect to secure card page.
   * Used when user clicks the "Switch to a credit card" button.
   * Memoized with useCallback to provide a stable reference.
   */
  const triggerRedirect = useCallback(() => {
    if (!purchaserId) return;

    redirectToSecureCardPage({
      retailerId,
      purchaserId,
      supplierId,
      paymentRequestId,
      invoiceId,
      userId,
      token,
      canSwitch,
      totalOutstandingInvoices,
    });
  }, [
    retailerId,
    purchaserId,
    supplierId,
    paymentRequestId,
    invoiceId,
    userId,
    token,
    canSwitch,
    totalOutstandingInvoices,
  ]);

  // Handle automatic redirect via useEffect to avoid side effects during render
  useEffect(() => {
    if (
      shouldRedirectToSecureCardPage &&
      !hasRedirected.current &&
      purchaserId
    ) {
      hasRedirected.current = true;
      triggerRedirect();
    }
  }, [shouldRedirectToSecureCardPage, purchaserId, triggerRedirect]);

  return {
    isPciSecureCardFormEnabled,
    shouldRedirectToSecureCardPage,
    triggerRedirect,
  };
};
